import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';

import * as adminApi from '../../api/admin';
import type { AuditEvent, AuditFilters } from '../../api/admin';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect } from '../../components/SearchableSelect';
import { useTableSort, sortRows } from '../../util/tableSort';
import { useTenantNames } from '../../util/useTenantNames';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];

type AuditSortKey =
  | 'created_at'
  | 'action'
  | 'actor'
  | 'entity_type'
  | 'entity_id'
  | 'tenant_id'
  | 'ip_address';

/**
 * Audit-trail viewer (superadmin only).
 *
 * Distinct from /admin/logs: this page only shows *intentional*
 * business events recorded by `services/audit.record()` — tenant
 * created/updated/soft-deleted, invitation created/accepted, login.
 * Every row has a structured action + entity + actor, and for
 * update events the before/after JSON snapshot is expandable inline
 * so you can see what changed.
 *
 * Same polling cadence as system logs (5s) and the same "load delta
 * since newest" optimisation.
 */

const POLL_MS = 5_000;

// Known actions ("all" is pinned separately). Adding a new action backend-
// side doesn't require updating this list — the dropdown is only a
// convenience for common filters; the server accepts any string.
const ACTIONS = [
  'user.registered',
  'user.logged_in',
  'user.login_failed',
  'tenant.created',
  'tenant.updated',
  'tenant.soft_deleted',
  'invitation.created',
  'invitation.accepted',
  'invitation.revoked',
];
const ACTION_OPTIONS = ACTIONS.map((a) => ({ value: a, label: a }));
const ACTION_PINNED = [{ value: '', label: '(all)' }];

function actionColor(
  action: string,
): 'default' | 'success' | 'info' | 'warning' | 'error' {
  if (action.endsWith('.created') || action.endsWith('.registered'))
    return 'success';
  if (action.endsWith('.logged_in') || action.endsWith('.accepted'))
    return 'info';
  if (action.endsWith('.updated')) return 'warning';
  if (action.endsWith('.deleted') || action.endsWith('.soft_deleted'))
    return 'error';
  // Failed sign-ins / second-factor checks — the rows an operator is
  // scanning for when they open this page after an alert.
  if (action.endsWith('_failed')) return 'error';
  return 'default';
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function shortId(uuid: string | null): string {
  return uuid ? uuid.slice(0, 8) : '—';
}

/** RFC-4180-ish CSV cell: wrap in quotes, double internal quotes. */
function csvCell(v: unknown): string {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}

/**
 * Export the currently-loaded audit rows to CSV — what auditors reach for.
 * Exports the current view (page); the `total` caption tells the operator how
 * many more exist behind pagination.
 */
function exportAuditCsv(
  rows: AuditEvent[],
  tenantName: (id: string | null | undefined) => string | null,
): void {
  const header = [
    'time',
    'action',
    'actor',
    'entity_type',
    'entity_id',
    'tenant',
    'ip',
    'metadata',
  ];
  const body = rows.map((r) =>
    [
      formatTs(r.created_at),
      r.action,
      r.actor_name ?? r.actor_user_id ?? '',
      r.entity_type ?? '',
      r.entity_id ?? '',
      tenantName(r.tenant_id) ?? r.tenant_id ?? '',
      r.ip_address ?? '',
      r.metadata ? JSON.stringify(r.metadata) : '',
    ]
      .map(csvCell)
      .join(','),
  );
  const csv = [header.map(csvCell).join(','), ...body].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit-trail.csv';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Row({
  row,
  tenantName,
  showTenant,
}: {
  row: AuditEvent;
  tenantName: (id: string | null | undefined) => string | null;
  showTenant: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasDetails =
    row.before !== null || row.after !== null || row.metadata !== null;
  // Columns: time, action, actor, entity, entity-id, [tenant], ip, expand.
  const colSpan = showTenant ? 8 : 7;

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', width: 160 }}>
          {formatTs(row.created_at)}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={row.action}
            color={actionColor(row.action)}
            variant={row.action.endsWith('.updated') ? 'outlined' : 'filled'}
          />
        </TableCell>
        <TableCell sx={{ fontSize: '0.85rem' }}>
          <Tooltip title={row.actor_user_id ?? 'system'}>
            <span>
              {row.actor_name ??
                (row.actor_user_id ? shortId(row.actor_user_id) : '—')}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.entity_type ?? '—'}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          <Tooltip title={row.entity_id ?? ''}>
            <span>{shortId(row.entity_id)}</span>
          </Tooltip>
        </TableCell>
        {showTenant && (
          <TableCell sx={{ fontSize: '0.85rem' }}>
            <Tooltip title={row.tenant_id ?? ''}>
              <span>{tenantName(row.tenant_id) ?? shortId(row.tenant_id)}</span>
            </Tooltip>
          </TableCell>
        )}
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.ip_address ?? '—'}
        </TableCell>
        <TableCell sx={{ width: 40, p: 0 }}>
          {hasDetails && (
            <IconButton
              size="small"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? 'Collapse row' : 'Expand row'}
            >
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ pt: 0, pb: 0, borderBottom: 'none' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Stack direction="row" spacing={3} sx={{ py: 2 }}>
                {row.before !== null && (
                  <Paper variant="outlined" sx={{ p: 1, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      before
                    </Typography>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {JSON.stringify(row.before, null, 2)}
                    </pre>
                  </Paper>
                )}
                {row.after !== null && (
                  <Paper variant="outlined" sx={{ p: 1, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      after
                    </Typography>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {JSON.stringify(row.after, null, 2)}
                    </pre>
                  </Paper>
                )}
                {row.metadata !== null && (
                  <Paper variant="outlined" sx={{ p: 1, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      metadata
                    </Typography>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {JSON.stringify(row.metadata, null, 2)}
                    </pre>
                  </Paper>
                )}
              </Stack>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function AuditTrail({
  embedded = false,
  actionsSlot = null,
  tenantId,
}: {
  embedded?: boolean;
  actionsSlot?: HTMLElement | null;
  /** When set, shows ONLY this tenant's audit events via the tenant-scoped
   *  endpoint (tenant_admin surface). The tenant column is hidden and the
   *  cross-tenant roster fetch is skipped. */
  tenantId?: string;
} = {}) {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  // Entity-type text only queries on Apply / Enter (mirrors the filter box).
  const [appliedEntityType, setAppliedEntityType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Newest-first by default; every data column is click-to-sort (within
  // the current page).
  const sort = useTableSort<AuditSortKey>('created_at', 'desc');
  const showTenant = !tenantId;
  const tenantName = useTenantNames(showTenant);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const common = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(action ? { action } : {}),
        ...(appliedEntityType.trim()
          ? { entity_type: appliedEntityType.trim() }
          : {}),
      };
      const res = tenantId
        ? await adminApi.listTenantAuditEvents(tenantId, common)
        : await adminApi.listAuditEvents(common as AuditFilters);
      setRows(res.items);
      setTotal(res.total);
    } catch (e: unknown) {
      setError(
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to load audit trail',
      );
    } finally {
      setLoading(false);
    }
  }, [action, appliedEntityType, page, rowsPerPage, tenantId]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  // Auto-refresh only the newest page — see SystemLogs for the rationale.
  useEffect(() => {
    if (!autoRefresh || page !== 0) return;
    const id = window.setInterval(() => {
      void fetchPage();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, page, fetchPage]);

  const changeAction = (next: string) => {
    setAction(next);
    setPage(0);
  };

  const applyEntityType = () => {
    setAppliedEntityType(entityType);
    setPage(0);
  };

  const controls = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
      <SearchableSelect
        id="audit-action"
        label="Action"
        value={action}
        onChange={changeAction}
        options={ACTION_OPTIONS}
        pinned={ACTION_PINNED}
        sx={{ minWidth: 200 }}
      />
      <TextField
        size="small"
        label="Entity type (e.g. tenant)"
        value={entityType}
        onChange={(e) => setEntityType(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') applyEntityType();
        }}
        sx={{ width: 220 }}
      />
      <Button variant="outlined" onClick={applyEntityType}>
        Apply
      </Button>
      <Tooltip title="Export the current view as CSV">
        <span>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportAuditCsv(rows, tenantName)}
            disabled={rows.length === 0}
          >
            Export CSV
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Refresh now">
        <span>
          <IconButton
            onClick={() => void fetchPage()}
            disabled={loading}
            aria-label="Refresh audit trail"
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <FormControlLabel
        control={
          <Switch
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
        }
        label={`Auto-refresh ${POLL_MS / 1000}s`}
      />
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {actionsSlot ? (
        createPortal(controls, actionsSlot)
      ) : (
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          {embedded ? (
            <Box sx={{ flexGrow: 1 }} />
          ) : (
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              Audit trail
            </Typography>
          )}
          {controls}
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="caption" color="text.secondary">
        {total.toLocaleString()} audit entries match the current filters
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <SortHeader
                label="Time"
                col="created_at"
                sort={sort}
                sx={{ width: 160 }}
              />
              <SortHeader
                label="Action"
                col="action"
                sort={sort}
                sx={{ minWidth: 180 }}
              />
              <SortHeader
                label="Actor"
                col="actor"
                sort={sort}
                sx={{ width: 100 }}
              />
              <SortHeader
                label="Entity"
                col="entity_type"
                sort={sort}
                sx={{ width: 100 }}
              />
              <SortHeader
                label="Entity ID"
                col="entity_id"
                sort={sort}
                sx={{ width: 100 }}
              />
              {showTenant && (
                <SortHeader
                  label="Tenant"
                  col="tenant_id"
                  sort={sort}
                  sx={{ minWidth: 140 }}
                />
              )}
              <SortHeader
                label="IP"
                col="ip_address"
                sort={sort}
                sx={{ width: 120 }}
              />
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={showTenant ? 8 : 7} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No audit events yet. Records appear when users
                    register, log in, or change tenant state.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sortRows(
              rows,
              (row) => {
                switch (sort.orderBy) {
                  case 'created_at':
                    return row.created_at;
                  case 'action':
                    return row.action;
                  case 'actor':
                    return row.actor_name ?? row.actor_user_id;
                  case 'entity_type':
                    return row.entity_type;
                  case 'entity_id':
                    return row.entity_id;
                  case 'tenant_id':
                    return tenantName(row.tenant_id) ?? row.tenant_id;
                  case 'ip_address':
                    return row.ip_address;
                }
              },
              sort.order,
            ).map((row) => (
              <Row
                key={row.id}
                row={row}
                tenantName={tenantName}
                showTenant={showTenant}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
    </Stack>
  );
}
